import React, { useState, useEffect, useRef } from 'react';
import api from '../api/client';
import toast from 'react-hot-toast';
import { useAuthStore } from '../store/authStore';
import { Camera, Save, Lock, Eye, EyeOff, Trash2, ArrowLeft, Mail, Phone, Shield, Calendar, Building } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function Profile() {
  const { user, tenant, refreshUser } = useAuthStore();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '' });
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showPw, setShowPw] = useState({ current: false, new: false, confirm: false });
  const [saving, setSaving] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [tab, setTab] = useState('profile');

  useEffect(() => {
    if (user) {
      setForm({ first_name: user.first_name || '', last_name: user.last_name || '', phone: user.phone || '' });
      setAvatarUrl(user.avatar_url || null);
    }
  }, [user]);

  const handleSaveProfile = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await api.put('/auth/profile', form);
      await refreshUser();
      toast.success('Profile updated');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleChangePassword = async () => {
    if (!pwForm.current_password || !pwForm.new_password) { toast.error('All password fields are required'); return; }
    if (pwForm.new_password.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (pwForm.new_password !== pwForm.confirm_password) { toast.error('Passwords do not match'); return; }
    setSavingPw(true);
    try {
      await api.put('/auth/password', { current_password: pwForm.current_password, new_password: pwForm.new_password });
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
      toast.success('Password changed');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to change password'); }
    finally { setSavingPw(false); }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('avatar', file);
      const res = await api.put('/auth/avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAvatarUrl(res.data.avatar_url);
      await refreshUser();
      toast.success('Avatar updated');
    } catch (err) { toast.error(err.response?.data?.error || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const handleRemoveAvatar = async () => {
    if (!window.confirm('Remove your profile photo?')) return;
    try {
      await api.delete('/auth/avatar');
      setAvatarUrl(null);
      await refreshUser();
      toast.success('Avatar removed');
    } catch (err) { toast.error('Failed to remove avatar'); }
  };

  const getInitials = () => ((user?.first_name?.[0] || '') + (user?.last_name?.[0] || '')).toUpperCase();

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—';

  const tabs = [
    { id: 'profile', label: 'Profile' },
    { id: 'security', label: 'Security' },
  ];

  const PwInput = ({ label, field }) => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <div className="relative">
        <input
          type={showPw[field] ? 'text' : 'password'}
          value={pwForm[field]}
          onChange={(e) => setPwForm({ ...pwForm, [field]: e.target.value })}
          className="w-full px-3 py-2 pr-9 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
        />
        <button type="button" onClick={() => setShowPw({ ...showPw, [field]: !showPw[field] })}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
          {showPw[field] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="p-1.5 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="h-4 w-4 text-gray-500" />
        </button>
        <h1 className="text-lg font-bold text-gray-900">My Profile</h1>
      </div>

      {/* Profile card */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
        <div className="h-24 bg-gradient-to-r from-blue-500 via-blue-600 to-indigo-600" />
        <div className="px-6 pb-5 -mt-10">
          <div className="flex items-end gap-4">
            {/* Avatar */}
            <div className="relative group">
              <div className="h-20 w-20 rounded-xl border-4 border-white shadow-sm overflow-hidden bg-white">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="Avatar" className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-xl font-bold">
                    {getInitials()}
                  </div>
                )}
              </div>
              <button
                onClick={() => fileRef.current?.click()}
                className="absolute inset-0 rounded-xl bg-black/0 group-hover:bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all cursor-pointer"
              >
                <Camera className="h-5 w-5 text-white" />
              </button>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleAvatarUpload} />
              {uploading && (
                <div className="absolute inset-0 rounded-xl bg-black/40 flex items-center justify-center">
                  <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                </div>
              )}
            </div>
            <div className="flex-1 pb-1">
              <h2 className="text-lg font-bold text-gray-900">{user?.first_name} {user?.last_name}</h2>
              <div className="flex items-center gap-3 mt-0.5">
                {user?.role_name && (
                  <span className="text-xs font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">{user.role_name}</span>
                )}
                {user?.is_admin && (
                  <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                    <Shield className="h-2.5 w-2.5" /> Admin
                  </span>
                )}
              </div>
            </div>
            {avatarUrl && (
              <button onClick={handleRemoveAvatar} className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1 pb-1">
                <Trash2 className="h-3 w-3" /> Remove photo
              </button>
            )}
          </div>

          {/* Quick info row */}
          <div className="flex items-center gap-5 mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <Mail className="h-3 w-3" /> {user?.email}
            </div>
            {user?.phone && (
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <Phone className="h-3 w-3" /> {user.phone}
              </div>
            )}
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <Building className="h-3 w-3" /> {tenant?.name}
            </div>
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <Calendar className="h-3 w-3" /> Joined {formatDate(user?.created_at)}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
              tab === t.id ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>{t.label}</button>
        ))}
      </div>

      {/* Profile Tab */}
      {tab === 'profile' && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Personal Information</h3>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">First Name</label>
              <input type="text" value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Last Name</label>
              <input type="text" value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
              <input type="email" value={user?.email || ''} disabled
                className="w-full px-3 py-2 text-sm border border-gray-100 rounded-lg bg-gray-50 text-gray-400 cursor-not-allowed" />
              <p className="text-[10px] text-gray-400 mt-1">Contact admin to change email</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
              <input type="tel" value={form.phone} placeholder="(555) 123-4567"
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400" />
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <button onClick={handleSaveProfile} disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors">
              <Save className="h-3.5 w-3.5" /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* Security Tab */}
      {tab === 'security' && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <Lock className="h-4 w-4 text-gray-500" />
            <h3 className="text-sm font-semibold text-gray-900">Change Password</h3>
          </div>
          <div className="max-w-sm space-y-3">
            <PwInput label="Current Password" field="current_password" />
            <PwInput label="New Password" field="new_password" />
            <PwInput label="Confirm New Password" field="confirm_password" />
            {pwForm.new_password && pwForm.new_password.length < 8 && (
              <p className="text-[10px] text-red-500">Must be at least 8 characters</p>
            )}
            {pwForm.confirm_password && pwForm.new_password !== pwForm.confirm_password && (
              <p className="text-[10px] text-red-500">Passwords do not match</p>
            )}
          </div>
          <div className="flex justify-end pt-4">
            <button onClick={handleChangePassword} disabled={savingPw}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors">
              <Lock className="h-3.5 w-3.5" /> {savingPw ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
